import PageHeader from "@/components/layout/PageHeader";
import ContactBlock from "@/components/sections/ContactBlock";
import FAQSection from "@/components/sections/FAQSection";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Reusable template for individual service pages.
 * Props: overline, title, sub, image, services (string[]), benefits (string[]),
 * processSteps (optional [{title,text}]), faq (optional), serviceTitle (for default form)
 */
export default function ServicePageTemplate({
  overline,
  title,
  sub,
  image,
  services = [],
  benefits = [],
  processSteps = [],
  faq,
  serviceTitle = "Other",
  breadcrumbs,
}) {
  return (
    <>
      <PageHeader
        overline={overline}
        title={title}
        sub={sub}
        image={image}
        breadcrumbs={breadcrumbs || [
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: overline || title },
        ]}
      />

      {services.length > 0 && (
        <section className="section-pad" data-testid="service-list-section">
          <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="overline mb-3">What's included</p>
              <h2 className="heading-2">Services we provide.</h2>
            </div>
            <div className="md:col-span-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {services.map((s, i) => (
                  <li key={i} data-testid={`service-item-${i}`} className="flex items-start gap-3 border-b border-brand-line pb-3">
                    <Check className="w-4 h-4 mt-1 text-brand-olive shrink-0" strokeWidth={2.5} />
                    <span className="text-brand-ink font-medium">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {processSteps.length > 0 && (
        <section className="section-pad bg-brand-limestone" data-testid="process-section">
          <div className="container-page">
            <p className="overline mb-3">Our process</p>
            <h2 className="heading-2 mb-12 max-w-2xl">Six clear steps. No surprises.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {processSteps.map((step, i) => (
                <div key={i} data-testid={`process-step-${i}`} className="card-base p-6">
                  <p className="font-heading text-5xl font-extrabold tracking-tighter text-brand-olive">0{i + 1}</p>
                  <h3 className="heading-3 mt-4">{step.title}</h3>
                  <p className="body-sm mt-2">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {benefits.length > 0 && (
        <section className="section-pad" data-testid="benefits-section">
          <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <p className="overline mb-3">Why this matters</p>
              <h2 className="heading-2">Real benefits — not just feature lists.</h2>
              <Link to="/services" className="btn-secondary mt-8 inline-flex">Other services</Link>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-line border border-brand-line rounded-md overflow-hidden">
                {benefits.map((b, i) => (
                  <div key={i} data-testid={`benefit-${i}`} className="bg-white p-6">
                    <p className="font-heading text-base font-bold text-brand-ink">{b}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {faq && <FAQSection items={faq} />}

      <ContactBlock source={`service_${serviceTitle.toLowerCase().replace(/\s+/g, "_")}`} />
    </>
  );
}
